import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import React from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import * as Yup from "yup";
import { HealthDeparmentNumberLookup } from "../../constants/AccountingNumbers";
import FormInput from "../Form/FormInput";

const validationSchema = Yup.object({
  name: Yup.string().required("Standortname eingeben"),
  locationId: Yup.string().required("Standort-ID eingeben"),
  email: Yup.string().email(),
  phone: Yup.string(),
  address: Yup.object()
    .shape({
      street: Yup.string().nullable(),
      number: Yup.string().nullable(),
      city: Yup.string().nullable(),
      zip: Yup.string().nullable(),
    })
    .nullable(),
  healthDepartmentEmail: Yup.string()
    .email()
    .nullable()
    .when("enableAutoEmail", {
      is: (enableAutoEmail: boolean) => enableAutoEmail === true,
      then: Yup.string().required(
        "Sie müssen eine Email für das Gesundheitsamt angeben wenn automatische Übermittlung aktiviert werden soll"
      ),
    }),
  healthDeparmentNumber: Yup.string(),
  enableAutoEmail: Yup.boolean(),
});

export interface LocationFormValues {
  name: string;
  address?: {
    street?: string;
    number?: string;
    city?: string;
    zip?: string;
  };
  locationId: string;
  email?: string;
  phone?: string;
  healthDepartmentEmail?: string;
  healthDeparmentNumber?: string;
  enableAutoEmail?: boolean;
}
export type LocationFormFunction = (values: LocationFormValues) => Promise<any>;

interface Props {
  onSubmit: LocationFormFunction;
  loading?: boolean;
  initi?: LocationFormValues;
}

const initialValues: LocationFormValues = {
  name: "",
  locationId: "",
  email: "",
  phone: "",
};
const useStyles = makeStyles((theme) => ({
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 250,
  },
  streetWrapper: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  street: {
    marginRight: 30,
  },
}));
const LocationForm: React.FC<Props> = ({ onSubmit, loading, initi }) => {
  const classes = useStyles();
  const methods = useForm<LocationFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: { ...initialValues, ...initi },
    mode: "all",
  });
  const { handleSubmit, control } = methods;
  return (
    <FormProvider {...methods}>
      <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
        <FormInput
          variant="outlined"
          margin="normal"
          required
          fullWidth
          name="name"
          label="Standort Name"
        />
        <FormInput
          variant="outlined"
          margin="normal"
          fullWidth
          name="email"
          label="Betreuer Email"
        />
        <FormInput
          variant="outlined"
          margin="normal"
          fullWidth
          name="phone"
          label="Telefonnummer"
        />
        <FormInput
          variant="outlined"
          margin="normal"
          fullWidth
          name="locationId"
          label="Standort-ID"
          required
        />
        <Typography>Auto Reporting</Typography>
        <Divider />
        <FormInput
          variant="outlined"
          margin="normal"
          fullWidth
          name="healthDepartmentEmail"
          label="Gesundheitsamt eMail"
        />
        <Controller
          name="healthDeparmentNumber"
          control={control}
          render={({ field: { onChange, value } }) => (
            <FormControl fullWidth margin="normal">
              <InputLabel
                id="select-healthDeparmentNumber-label"
                style={{ marginLeft: 10 }}
              >
                Abrechnungsnummer
              </InputLabel>
              <Select
                labelId="select-healthDeparmentNumber-label"
                id="select-healthDeparmentNumber-select"
                value={value}
                variant="outlined"
                label="Abrechnungsnummer"
                onChange={onChange}
              >
                <MenuItem value={undefined}>Keine Abrechnungsnummer</MenuItem>
                {Object.keys(HealthDeparmentNumberLookup).map((key) => {
                  return (
                    <MenuItem value={key}>
                      {HealthDeparmentNumberLookup[key]}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          )}
        />
        <Controller
          name="enableAutoEmail"
          control={control}
          render={({ field: { onChange, value } }) => (
            <FormControlLabel
              control={
                <Checkbox
                  checked={value}
                  onChange={onChange}
                  name="enableAutoEmail"
                  color="primary"
                />
              }
              label="Übermitteln der Testsentnahmen automatisiert zum 01. des Folgemonats aktiviert?"
            />
          )}
        />
        <Typography style={{ marginTop: 20 }}>Adresse</Typography>
        <Divider />
        <div className={classes.streetWrapper}>
          <FormInput
            variant="outlined"
            margin="normal"
            fullWidth
            name="address.street"
            label="Straße"
            className={classes.street}
          />
          <FormInput
            variant="outlined"
            margin="normal"
            fullWidth
            name="address.number"
            label="Hausnummer"
          />
        </div>
        <FormInput
          variant="outlined"
          margin="normal"
          fullWidth
          name="address.city"
          label="Stadt"
        />
        <FormInput
          variant="outlined"
          margin="normal"
          fullWidth
          name="address.zip"
          label="PLZ"
        />
        <Button
          type="submit"
          fullWidth
          disabled={loading}
          variant="contained"
          color="primary"
          className={classes.submit}
          onClick={handleSubmit(onSubmit)}
        >
          Speichern
        </Button>
      </form>
    </FormProvider>
  );
};

export default LocationForm;
